import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Observable, switchMap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthenticationService } from '../services/authentication.service';

/** URLs d'auth qu'on n'essaie pas de relancer après un 401. */
const AUTH_ENDPOINTS_NO_RETRY = [
  '/auth/login',
  '/auth/refresh',
  '/auth/logout',
];

const isAuthEndpoint = (url: string): boolean =>
  AUTH_ENDPOINTS_NO_RETRY.some((path) => url.includes(path));

/**
 * Clone la requête en remplaçant l'Authorization par le nouveau bearer.
 * Nécessaire car au moment du retry on est en aval de
 * `authorizationInterceptor` : il ne réinjectera pas le header tout seul.
 */
const cloneWithNewToken = (
  request: HttpRequest<unknown>,
  accessToken: string
): HttpRequest<unknown> =>
  request.clone({
    setHeaders: { Authorization: `Bearer ${accessToken}` },
  });

export const errorInterceptor = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authenticationService = inject(AuthenticationService);
  const nzNotificationService = inject(NzNotificationService);
  const translateService = inject(TranslateService);

  return next(request).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status === 401) {
        if (isAuthEndpoint(request.url)) {
          authenticationService.clearSession();
          return throwError(() => err);
        }

        return authenticationService.refresh().pipe(
          switchMap((token) =>
            next(cloneWithNewToken(request, token.accessToken ?? ''))
          ),
          catchError((refreshErr) => throwError(() => refreshErr))
        );
      }

      switch (err.status) {
        case 403:
        case 408:
        case 415:
          nzNotificationService.warning(
            translateService.instant('NOTIFICATION.ERROR'),
            translateService.instant('NOTIFICATION.INTERNAL_ERROR')
          );
          break;
        case 409:
          nzNotificationService.warning(
            translateService.instant('NOTIFICATION.WARNING'),
            translateService.instant('NOTIFICATION.CONFLICT')
          );
          break;
        case 500:
          nzNotificationService.error(
            translateService.instant('NOTIFICATION.ERROR'),
            translateService.instant('NOTIFICATION.INTERNAL_ERROR')
          );
          break;
      }

      return throwError(() => err);
    })
  );
};
